"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { useConfettiStore } from "@/stores/confetti-store"
import { toast } from "sonner"

import { ConfettiStyle, useConfettiCable } from "@/hooks/use-confetti-cable"

import { ConfettiCanvas } from "./confetti-canvas"

export function GlobalConfetti() {
  const [activeStyle, setActiveStyle] = useState<ConfettiStyle | null>(null)
  const { style: storeStyle, message: storeMessage, clear } = useConfettiStore()
  const lastToastTimeRef = useRef<number>(0)

  const showToast = useCallback((message: string) => {
    const now = Date.now()
    // Debounce toasts - ignore if one was shown in the last 500ms
    if (now - lastToastTimeRef.current < 500) return
    lastToastTimeRef.current = now
    toast.success(message)
  }, [])

  const handleConfetti = useCallback(
    (style: ConfettiStyle, message?: string) => {
      // Only show confetti if the user is actively viewing the page
      if (document.visibilityState !== "visible") return

      setActiveStyle(style)
      if (message?.trim()) {
        showToast(message)
      }
    },
    [showToast]
  )

  const handleComplete = useCallback(() => {
    setActiveStyle(null)
  }, [])

  useConfettiCable(handleConfetti)

  useEffect(() => {
    if (storeStyle) {
      setActiveStyle(storeStyle)
      if (storeMessage?.trim()) {
        showToast(storeMessage)
      }
      clear()
    }
  }, [storeStyle, storeMessage, clear, showToast])

  return <ConfettiCanvas style={activeStyle} onComplete={handleComplete} />
}
